import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store';
import { i18nService } from '../services/i18n';
import BoclawLogo from '../components/icons/BoclawLogo';
import SkillCenterIcon from '../components/icons/SkillCenterIcon';
import MarkdownContent from '../components/MarkdownContent';
import { SkillsView } from '../components/skills';
import { ScheduledTasksView } from '../components/scheduledTasks';
import type { AuthState } from '../types/auth';
import {
  relayService, platformLabel, getTargetClient, type RelayClientInfo,
} from '../services/relay';
import {
  setSelectedClient, setSessions, setConfig, setMessages, setCurrentSessionId,
  upsertSession, setStatus, setStreaming, clearPermission, removeSession, patchSession,
} from '../store/slices/codingSlice';
import type { CodingMessage, CodingSessionSummary } from './types';
import CodingQuestionPrompt, { parseAskUserQuestions } from './CodingQuestionPrompt';
import FollowupDrawer from '../components/common/FollowupDrawer';
import CwdPickerModal from '../components/ui/CwdPickerModal';
import TerminalPanel from '../components/cowork/TerminalPanel';
import { coworkService } from '../services/cowork';
import { isPrimaryChatModel } from '../utils/modelCapabilityLabels';
import { setSelectedModel } from '../store/slices/modelSlice';
import { configService } from '../services/config';
import { authService } from '../services/auth';
import { CODING_E2B_TEMPLATE_ID } from '../constants/app';

// 从 sandbox-images 里挑「编程专用」镜像：优先按 E2B Template ID 命中后台「编程沙箱」
// (boclaw-coder)，否则按名称兜底（含「编程」/coder/coding），都没有再回退到首个启用项。
// 仅启用(isEnabled)的镜像参与；全禁用时退回原始列表首项。不影响 Cowork 的镜像选择。
function pickCodingImage(images: any[]): any | null {
  if (!Array.isArray(images) || images.length === 0) return null;
  const enabled = images.filter((i) => i && i.isEnabled !== false);
  const pool = enabled.length ? enabled : images;
  return (
    pool.find((i) => i.e2bTemplateId && i.e2bTemplateId === CODING_E2B_TEMPLATE_ID) ||
    pool.find((i) => /编程|cod(?:er|ing)/i.test(`${i.name ?? ''} ${i.imageName ?? ''}`)) ||
    pool[0] ||
    null
  );
}

// 跟随全站 accent 变量（组织品牌可覆盖），缺省回退到默认蓝
const BLUE = 'var(--accent-color, #3B82F6)';
const BLUE_TINT = 'color-mix(in srgb, var(--accent-color, #3B82F6) 12%, transparent)';
const cardShadow = '0 18px 44px rgba(28,48,98,.12), 0 4px 14px rgba(28,48,98,.06)';

// 云端环境固定客户端 id（请求走 REST 旁路，clientId 仅作前端选择/路由前缀用，需以 web- 开头）
const CLOUD_CLIENT_ID = 'web-cloud';

function toast(msg: string) {
  window.dispatchEvent(new CustomEvent('app:showToast', { detail: msg }));
}

interface CodingViewProps {
  onBack: () => void;
  isMobile?: boolean;
  authState?: AuthState | null;
  onShowSettings?: () => void;
}

// ── 小图标 ──
const Ic = {
  term: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]"><path d="m5 8 4 4-4 4M12 16h7" /></svg>),
  clock: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]"><circle cx="12" cy="12" r="9" /><path d="M12 12V7M12 12l3.5 2" /></svg>),
  folder: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>),
  caretd: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3"><path d="m6 9 6 6 6-6" /></svg>),
  shield: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6z" /></svg>),
  model: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><rect x="5" y="5" width="14" height="14" rx="3" /><path d="M9 9h6v6H9z" /></svg>),
  send: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]"><path d="M12 19V5M5 12l7-7 7 7" /></svg>),
  stop: (<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-3.5 w-3.5"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>),
  back: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m15 18-6-6 6-6" /></svg>),
  laptop: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><rect x="4" y="5" width="16" height="11" rx="2" /><path d="M2 20h20" /></svg>),
  cloud: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M7 18a4 4 0 0 1-.5-7.97A5.5 5.5 0 0 1 17 9.5a3.5 3.5 0 0 1 .5 6.96Z" /></svg>),
  plus: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 5v14M5 12h14" /></svg>),
  search: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>),
  trash: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" /></svg>),
  sidebar: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16" /></svg>),
  gear: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>),
  chevron: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m9 6 6 6-6 6" /></svg>),
  zap: (<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-3 w-3"><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg>),
  dots: (<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-4 w-4"><circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" /></svg>),
  pin: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M9 4h6l-1 6 3 2.5V15H7v-2.5L10 10z" /><path d="M12 15v5" /></svg>),
  edit: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>),
  check: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="m5 12 5 5L20 6" /></svg>),
  checkCircle: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="h-[15px] w-[15px]"><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></svg>),
bulb: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[17px] w-[17px]"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.3 1 2.1V16h6v-.4c0-.8.4-1.5 1-2.1A6 6 0 0 0 12 3Z" /></svg>),
wrench: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-[15px] w-[15px]"><path d="M14.7 6.3a4 4 0 0 0-5.2 5l-6 6a1.5 1.5 0 0 0 2.1 2.1l6-6a4 4 0 0 0 5-5.2l-2.6 2.6-2.1-.6-.6-2.1 2.6-2.6Z" /></svg>),
};

// 距今相对时间
function relTime(ts: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return i18nService.t('codeJustNow');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// 会话状态 → 文案
function statusText(st: string): string {
  switch (st) {
    case 'running': return i18nService.t('codeStatusRunning');
    case 'stopped': return i18nService.t('codeStatusStopped');
    case 'error': return i18nService.t('codeStatusError');
    default: return i18nService.t('codeStatusIdle');
  }
}

// 执行模式（权限/操作模式）—— desktop config.permissionModes 为空时的兜底取值
const DEFAULT_PERMISSION_MODES = ['default', 'acceptEdits', 'plan', 'bypassPermissions'];

// 执行模式 → 文案（用现成 i18n key，未知值回退原始字符串）
function permModeLabel(mode: string): string {
  switch (mode) {
    case 'auto': return i18nService.t('codePermModeAuto');
    case 'default': return i18nService.t('codePermModeDefault');
    case 'acceptEdits': return i18nService.t('codePermModeAcceptEdits');
    case 'plan': return i18nService.t('codePermModePlan');
    case 'bypassPermissions': return i18nService.t('codePermModeBypassPermissions');
    default: return mode;
  }
}

// 加载转圈（与客户端一致的旋转圆环）
function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent align-[-2px]"
      style={{ width: size, height: size, color: BLUE }}
    />
  );
}

// Code 侧栏主导航项（编程会话 / Skills / 定时任务）
function CodeNavItem({ icon, label, active, onClick }: {
  icon: React.ReactNode; label: string; active?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm ${active ? 'font-medium' : 'text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover'}`}
      style={active ? { color: BLUE, background: BLUE_TINT } : undefined}>
      {icon}{label}
    </button>
  );
}

// 会话三个点菜单的菜单项
function MenuRow({ icon, label, onClick, danger }: {
  icon: React.ReactNode; label: string; onClick: (e: React.MouseEvent) => void; danger?: boolean;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover ${danger ? 'text-red-500' : 'text-claude-text dark:text-claude-darkText'}`}>
      {icon}{label}
    </button>
  );
}

// 本地置顶（按设备隔离，存 localStorage —— 置顶是 web 端本地偏好，不依赖 desktop）
function pinnedKey(clientId: string | null): string { return `coding_pinned_${clientId || 'none'}`; }
function loadPinned(clientId: string | null): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(pinnedKey(clientId)) || '[]')); } catch { return new Set(); }
}
function savePinned(clientId: string | null, set: Set<string>): void {
  try { localStorage.setItem(pinnedKey(clientId), JSON.stringify([...set])); } catch { /* noop */ }
}

const CodingView: React.FC<CodingViewProps> = ({ onBack, isMobile, authState, onShowSettings }) => {
  const dispatch = useDispatch();
  const {
    selectedClientId, sessions, currentSessionId,
    messagesBySession, statusBySession, pendingPermissionBySession, isStreaming, config,
  } = useSelector((s: RootState) => s.coding);
  // 全局模型列表（云端模式的模型选择来源于此，与 Cowork 共享同一份配置）
  const globalModels = useSelector((s: RootState) => s.model.availableModels);
  const globalSelectedModel = useSelector((s: RootState) => s.model.selectedModel);

  useLayoutEffect(() => {
    authService.loadSavedModelsToRedux();
  }, []);

  const [relayClients, setRelayClients] = useState<RelayClientInfo[]>([]);
  const [input, setInput] = useState('');
  const [deviceMenu, setDeviceMenu] = useState(false);
  const [modelMenu, setModelMenu] = useState(false);
  const [modeMenu, setModeMenu] = useState(false);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [nav, setNav] = useState<'sessions' | 'skills' | 'tasks'>('sessions');
  const [manageMode, setManageMode] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  // 云端环境默认沙箱（profile/image），与 CoworkView 一致取列表首项
  const [cloudSandbox, setCloudSandbox] = useState<{ profileId: string | null; imageId: string | null }>({ profileId: null, imageId: null });
  // 新建会话时在 composer 选好的模型（会话已存在则走 setCodingModel）
  const [pickedModel, setPickedModel] = useState<string | null>(null);
  // 新建会话时在 composer 选好的执行模式（会话已存在则走 setCodingPermissionMode）
  const [pickedPermissionMode, setPickedPermissionMode] = useState<string | null>(null);
  // 新建桌面会话时选定的工作目录（null = 用 desktop 默认目录）；仅桌面会话可选
  const [pickedCwd, setPickedCwd] = useState<string | null>(null);
  const [cwdPickerOpen, setCwdPickerOpen] = useState(false);
  // 新建云端会话时可选：从 git 仓库开始（沙箱起好后自动 clone）
  const [gitRepoUrl, setGitRepoUrl] = useState('');
  const [gitBranch, setGitBranch] = useState('');
  const [gitUsername, setGitUsername] = useState('');
  const [gitPassword, setGitPassword] = useState('');
  const anyMenuOpen = deviceMenu || modelMenu || modeMenu || menuOpenId !== null;
  const closeMenus = () => { setDeviceMenu(false); setModelMenu(false); setModeMenu(false); setMenuOpenId(null); };
  const scrollRef = useRef<HTMLDivElement>(null);

  const accountLabel = authState?.user.displayName || authState?.user.username || 'BoCoder';
  const phone = authState?.user.phoneNumber?.replace(/^(\d{3})\d{4}(\d+)$/, '$1****$2');

  // 在线 desktop 设备（排除 web 自身）
  const desktops = useMemo(
    () => relayClients.filter((c) => c.platform !== 'web' && c.online),
    [relayClients],
  );
  // 云端环境：始终可用（请求走 REST 旁路 /api/coding，不依赖 relay 注册），
  // 因此合成一个稳定的虚拟客户端，避免随主 /ws 重连而闪烁/消失。
  const cloudClient = useMemo<RelayClientInfo>(() => ({
    clientId: CLOUD_CLIENT_ID,
    deviceName: 'Cloud',
    productType: 'bocoder',
    platform: 'web',
    appVersion: '',
    online: true,
    registeredAt: 0,
    lastSeenAt: 0,
  }), []);
  const selectedClient = useMemo(
    () => (selectedClientId === cloudClient.clientId
      ? cloudClient
      : relayClients.find((c) => c.clientId === selectedClientId) || null),
    [relayClients, selectedClientId, cloudClient],
  );
  const isCloud = !!selectedClient && selectedClient.platform === 'web';

  const currentSession = useMemo(
    () => sessions.find((s) => s.id === currentSessionId) || null,
    [sessions, currentSessionId],
  );
  const filteredSessions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => (s.title || '').toLowerCase().includes(q));
  }, [sessions, query]);
  // 置顶优先 + 按更新时间倒序
  const sortedSessions = useMemo(() => {
    const arr = [...filteredSessions];
    arr.sort((a, b) => {
      const pa = pinned.has(a.id) ? 1 : 0;
      const pb = pinned.has(b.id) ? 1 : 0;
      if (pa !== pb) return pb - pa;
      return (b.updatedAt || 0) - (a.updatedAt || 0);
    });
    return arr;
  }, [filteredSessions, pinned]);
  const messages = currentSessionId ? (messagesBySession[currentSessionId] ?? []) : [];
  const status = currentSessionId
    ? (statusBySession[currentSessionId] ?? currentSession?.status ?? 'idle')
    : 'idle';
  const running = status === 'running' || (isStreaming && !!currentSessionId);
  const pendingPermission = currentSessionId ? pendingPermissionBySession[currentSessionId] : null;

  // 终端一旦打开过就保持挂载(用 hidden 切换显隐),收起再打开能保留同一个 shell
  // 会话与完整滚动历史。切换会话 / 沙箱停止时才真正卸载。
  const [terminalOpenedOnce, setTerminalOpenedOnce] = useState(false);

  // 切换会话时收起并卸载终端,避免连到上一个会话的 PTY
  useEffect(() => { setShowTerminal(false); setTerminalOpenedOnce(false); }, [currentSessionId]);

  // 终端面板宽度(像素),可通过分隔条拖拽调整
  const splitRowRef = useRef<HTMLDivElement | null>(null);
  const [terminalWidth, setTerminalWidth] = useState(480);
  const onTerminalDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const row = splitRowRef.current;
    if (!row) return;
    const onMove = (ev: MouseEvent) => {
      const rect = row.getBoundingClientRect();
      const min = 320;
      const max = Math.max(min, rect.width - 360); // 给左侧聊天至少留 360
      const w = Math.min(Math.max(rect.right - ev.clientX, min), max);
      setTerminalWidth(w);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // 轮询沙箱是否在运行(vncUrl 非空 = 后端有活的 sandboxContainerSession,
  // 也正是终端能连上的条件)。据此决定是否展示终端按钮 —— 会话未运行时不展示。
  const [sandboxLive, setSandboxLive] = useState(false);
  useEffect(() => {
    setSandboxLive(false);
    if (!isCloud || !currentSessionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      if (cancelled || !currentSessionId) return;
      const url = await coworkService.getSessionVncUrl(currentSessionId);
      if (cancelled) return;
      const live = !!url;
      setSandboxLive(live);
      // 沙箱停止后无法保留 PTY,卸载终端(下次起来重新连一个新 shell)
      if (!live) { setShowTerminal(false); setTerminalOpenedOnce(false); }
      timer = setTimeout(poll, 4000);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [isCloud, currentSessionId]);

  // ── 模型列表与当前模型（云端用全局模型列表，desktop 用 config.get 下发）──
  const models = useMemo(() => {
    if (isCloud) return globalModels.filter(isPrimaryChatModel).map((m) => ({ id: m.id, name: m.name, provider: m.provider }));
    return config?.availableModels ?? [];
  }, [isCloud, globalModels, config]);
  // 云端：模型全局生效（runner 读 config.model.defaultModel），以全局选中为准；desktop：取会话 model。
  const curModelId = useMemo(() => {
    if (isCloud) return pickedModel || globalSelectedModel?.id || models[0]?.id || '';
    if (currentSession?.model) return currentSession.model;
    return models[0]?.id || '';
  }, [isCloud, pickedModel, globalSelectedModel, currentSession, models]);

  // ── 执行模式（仅 desktop/relay 会话用）：选项取 desktop config.permissionModes，缺省兜底 ──
  const permissionModes = useMemo(() => {
    const list = config?.permissionModes;
    return Array.isArray(list) && list.length ? list : DEFAULT_PERMISSION_MODES;
  }, [config]);
  // 已有会话取会话上的模式；新建会话用 composer 选定值，否则取列表首项。
  const curMode = useMemo(() => {
    if (currentSession?.permissionMode) return currentSession.permissionMode;
    return pickedPermissionMode ?? (permissionModes[0] || 'default');
  }, [currentSession, pickedPermissionMode, permissionModes]);

  // ── 进入页面默认新建会话（不恢复上次打开的会话）──
  useEffect(() => {
    dispatch(setCurrentSessionId(null));
    setInput('');
  }, []);

  // ── 连接 relay + 订阅设备列表 ──
  useEffect(() => {
    let alive = true;
    relayService.ensureConnected()
      .then(() => { if (alive) setRelayClients(relayService.getAllClients()); })
      .catch(() => { if (alive) setRelayClients(relayService.getAllClients()); });
    const unsub = relayService.subscribe(() => {
      if (alive) setRelayClients(relayService.getAllClients());
    });
    return () => { alive = false; unsub(); };
  }, []);

  // ── 默认选中「云端环境」；无云端时回退到第一台在线 desktop（不覆盖用户已有的有效手选）──
  useEffect(() => {
    const stillValid =
      selectedClientId &&
      (desktops.some((c) => c.clientId === selectedClientId) ||
        (cloudClient && selectedClientId === cloudClient.clientId));
    if (stillValid) return;
    if (cloudClient) dispatch(setSelectedClient(cloudClient.clientId));
    else if (desktops.length > 0) dispatch(setSelectedClient(desktops[0].clientId));
    else if (selectedClientId) dispatch(setSelectedClient(null));
  }, [desktops, cloudClient, selectedClientId, dispatch]);

  // ── 选中设备变化 → 拉会话列表 + config（顺带清空上一台设备选的工作目录，避免串台）──
  useEffect(() => {
    setPickedCwd(null);
    if (!selectedClientId) return;
    let alive = true;
    (async () => {
      try {
        const { sessions: list } = await relayService.listCodingSessions(selectedClientId);
        if (alive) dispatch(setSessions((list ?? []) as CodingSessionSummary[]));
      } catch {
        if (alive) toast(i18nService.t(isCloud ? 'codeToastCloudUnreachable' : 'codeToastDeviceUnreachable'));
      }
      try {
        const cfg = await relayService.getCodingConfig(selectedClientId);
        if (alive && cfg) dispatch(setConfig(cfg as any));
      } catch { /* config 拉取失败不阻塞 */ }
    })();
    return () => { alive = false; };
  }, [selectedClientId, isCloud, dispatch]);

  // ── 切换设备时加载该设备的本地置顶 ──
  useEffect(() => { setPinned(loadPinned(selectedClientId)); }, [selectedClientId]);

  // ── 云端模式：拉取默认沙箱 profile/image（取列表首项，与 CoworkView 一致）──
  useEffect(() => {
    if (!isCloud) return;
    if (cloudSandbox.profileId && cloudSandbox.imageId) return;
    let alive = true;
    const token = localStorage.getItem('boclaw_token');
    const headers: Record<string, string> = {
      'x-boclaw-user-id': localStorage.getItem('boclaw_user_id') || '',
      'x-boclaw-user-role': localStorage.getItem('boclaw_user_role') || '',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    (async () => {
      try {
        const [pr, ir] = await Promise.all([
          fetch('/api/cowork/sandbox-profiles', { headers }).then((r) => r.json()).catch(() => []),
          fetch('/api/cowork/sandbox-images', { headers }).then((r) => r.json()).catch(() => []),
        ]);
        if (!alive) return;
        // 镜像选「编程沙箱」(boclaw-coder)；profile 仍取首项（E2B profile 提供后端连接信息，
        // 镜像的 e2bTemplateId 在 E2bBackend 里优先于 profile 的 templateId 生效）。
        const codingImg = pickCodingImage(Array.isArray(ir) ? ir : []);
        const imageId = codingImg?.id != null ? String(codingImg.id) : null;
        const profileId = Array.isArray(pr) && pr[0]?.id != null ? String(pr[0].id) : null;
        setCloudSandbox({ profileId, imageId });
      } catch { /* 拉取失败时回退到服务端默认（可能拉不到镜像） */ }
    })();
    return () => { alive = false; };
  }, [isCloud, cloudSandbox.profileId, cloudSandbox.imageId]);

  // ── 消息更新自动滚到底 ──
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, currentSessionId]);

  // ── 打开会话 ──
  const openSession = useCallback(async (sessionId: string) => {
    if (!selectedClientId) return;
    setNav('sessions'); // 从 Skills / 定时任务 视图点会话时切回会话区
    dispatch(setCurrentSessionId(sessionId));
    relayService.claimCodingSession(sessionId);
    try {
      const { session, messages: msgs } = await relayService.getCodingSession(selectedClientId, sessionId);
      if (session) dispatch(upsertSession(session as CodingSessionSummary));
      dispatch(setMessages({ sessionId, messages: (msgs ?? []) as CodingMessage[] }));
    } catch {
      toast(i18nService.t('codeToastLoadSessionFailed'));
    }
  }, [selectedClientId, dispatch]);

  // ── 新建会话（清空当前选中，下次发送即 start）──
  const newSession = useCallback(() => {
    dispatch(setCurrentSessionId(null));
    setInput('');
  }, [dispatch]);

  // ── 删除会话 ──
  const deleteSession = useCallback(async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedClientId) return;
    if (!window.confirm(i18nService.t('codeDeleteConfirm'))) return;
    dispatch(removeSession(sessionId)); // 乐观移除
    try {
      await relayService.deleteCodingSession(selectedClientId, sessionId);
    } catch (err) {
      toast(`${i18nService.t('codeToastDeleteFailed')}: ${(err as Error).message}`);
    }
  }, [selectedClientId, dispatch]);

  // ── 管理模式：多选 / 全选 / 批量删除 ──
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const selectAllToggle = useCallback(() => {
    setSelectedIds((prev) => prev.size === filteredSessions.length ? new Set() : new Set(filteredSessions.map((s) => s.id)));
  }, [filteredSessions]);
  const batchDelete = useCallback(async () => {
    if (!selectedIds.size || !selectedClientId) return;
    if (!window.confirm(i18nService.t('codeBatchDeleteConfirm'))) return;
    const ids = [...selectedIds];
    ids.forEach((id) => dispatch(removeSession(id)));
    setSelectedIds(new Set());
    setManageMode(false);
    for (const id of ids) {
      try { await relayService.deleteCodingSession(selectedClientId, id); } catch { /* 已乐观移除 */ }
    }
  }, [selectedIds, selectedClientId, dispatch]);

  // ── 置顶（本地）/ 重命名（relay） ──
  const togglePin = useCallback((id: string) => {
    setPinned((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); savePinned(selectedClientId, n); return n; });
  }, [selectedClientId]);
  const renameSession = useCallback(async (s: CodingSessionSummary) => {
    if (!selectedClientId) return;
    const name = window.prompt(i18nService.t('codeRenamePrompt'), s.title || '');
    if (name == null) return;
    const title = name.trim();
    if (!title || title === s.title) return;
    dispatch(patchSession({ sessionId: s.id, title }));
    try { await relayService.renameCodingSession(selectedClientId, s.id, title); }
    catch (e) { toast(`${i18nService.t('codeToastRenameFailed')}: ${(e as Error).message}`); }
  }, [selectedClientId, dispatch]);

  // ── 发送 ──
  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || running) return;
    if (!selectedClientId) { toast(i18nService.t('codeToastNoDesktop')); return; }
    // 云端新建会话：仓库地址必填
    if (isCloud && !currentSessionId && !gitRepoUrl.trim()) { toast(i18nService.t('codeToastNoRepo')); return; }
    setInput('');
    try {
      if (!currentSessionId) {
        dispatch(setStreaming(true));
        const { session } = await relayService.startCodingSession(selectedClientId, {
          prompt: text,
          ...(isCloud
            ? {
                sandboxProfileId: cloudSandbox.profileId, sandboxImageId: cloudSandbox.imageId,
                model: curModelId || undefined,
                gitRepoUrl: gitRepoUrl.trim() || undefined,
                gitBranch: gitBranch.trim() || undefined,
                gitUsername: gitUsername.trim() || undefined,
                gitPassword: gitPassword || undefined,
              }
            : { permissionMode: curMode || undefined, cwd: pickedCwd || undefined }),
        });
        if (session?.id) {
          dispatch(upsertSession(session as CodingSessionSummary));
          // 记录仓库地址：优先用 desktop 回传字段，否则用本次创建时输入的 Git 仓库 URL
          const repo = (session as any).repoUrl || (session as any).gitRepoUrl || gitRepoUrl.trim();
          if (repo) dispatch(patchSession({ sessionId: session.id, repoUrl: repo }));
          dispatch(setCurrentSessionId(session.id));
          dispatch(setStatus({ sessionId: session.id, status: 'running' }));
          setPickedModel(null);
          setPickedPermissionMode(null);
          setPickedCwd(null);
          setGitRepoUrl(''); setGitBranch(''); setGitUsername(''); setGitPassword('');
          // 调用一次模型自动生成会话标题（后台执行，不阻塞）
          const sid = session.id;
          const clientId = selectedClientId;
          window.electron?.generateSessionTitle?.(text).then((title) => {
            const t = (title || '').trim();
            if (t && t !== 'New Session') {
              dispatch(patchSession({ sessionId: sid, title: t }));
              relayService.renameCodingSession(clientId, sid, t).catch(() => {});
            }
          }).catch(() => {});
        }
      } else {
        dispatch(setStreaming(true));
        dispatch(setStatus({ sessionId: currentSessionId, status: 'running' }));
        await relayService.continueCodingSession(selectedClientId, currentSessionId, text);
      }
    } catch (e) {
      dispatch(setStreaming(false));
      setInput(text); // 失败回填
      toast(`${i18nService.t('codeToastSendFailed')}: ${(e as Error).message}`);
    }
  }, [input, running, selectedClientId, currentSessionId, isCloud, cloudSandbox, curModelId, curMode, pickedCwd, gitRepoUrl, gitBranch, gitUsername, gitPassword, dispatch]);

  // ── 停止 ──
  const stop = useCallback(async () => {
    if (!selectedClientId || !currentSessionId) return;
    try {
      await relayService.stopCodingSession(selectedClientId, currentSessionId);
      dispatch(setStreaming(false));
    } catch (e) {
      toast(`${i18nService.t('codeToastStopFailed')}: ${(e as Error).message}`);
    }
  }, [selectedClientId, currentSessionId, dispatch]);

  // ── 权限响应 ──
  const respondPermission = useCallback(async (
    decision: 'allow' | 'deny' | 'allow_always',
    extra?: { updatedInput?: unknown },
  ) => {
    if (!pendingPermission || !currentSessionId) return;
    const targetId = getTargetClient(currentSessionId) || selectedClientId;
    if (!targetId) return;
    // 乐观清除
    dispatch(clearPermission({ sessionId: currentSessionId, requestId: pendingPermission.requestId }));
    try {
      await relayService.respondCodingPermission(targetId, currentSessionId, pendingPermission.requestId, decision, extra);
    } catch (e) {
      toast(`${i18nService.t('codeToastPermissionFailed')}: ${(e as Error).message}`);
    }
  }, [pendingPermission, currentSessionId, selectedClientId, dispatch]);

  // ── 切模型 / 权限模式 ──
  const changeModel = useCallback(async (model: string) => {
    setModelMenu(false);
    setPickedModel(model);
    // 云端模型选择与 Cowork 共用 Web 默认模型配置。
    if (isCloud) {
      const full = globalModels.find((m) => m.id === model);
      if (full) {
        dispatch(setSelectedModel(full));
        void configService.setDefaultModel(full.id)
          .catch((error) => console.error('Failed to save default model:', error));
      }
    }
    if (selectedClientId && currentSessionId) {
      dispatch(patchSession({ sessionId: currentSessionId, model }));
      try { await relayService.setCodingModel(selectedClientId, currentSessionId, model); } catch (e) { toast(`${i18nService.t('codeToastModelFailed')}: ${(e as Error).message}`); }
    }
  }, [isCloud, globalModels, selectedClientId, currentSessionId, dispatch]);

  // ── 切执行模式（权限/操作模式，仅 desktop 会话）──
  const changeMode = useCallback(async (mode: string) => {
    setModeMenu(false);
    setPickedPermissionMode(mode);
    if (selectedClientId && currentSessionId) {
      dispatch(patchSession({ sessionId: currentSessionId, permissionMode: mode }));
      try { await relayService.setCodingPermissionMode(selectedClientId, currentSessionId, mode); }
      catch (e) { toast(`${i18nService.t('codeToastModeFailed')}: ${(e as Error).message}`); }
    }
  }, [selectedClientId, currentSessionId, dispatch]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const curModelName = useMemo(() => {
    if (!curModelId) return 'Model';
    return models.find((x) => x.id === curModelId)?.name || curModelId;
  }, [curModelId, models]);

  return (
    <div className="flex-1 flex min-h-0 gap-2.5 p-2.5 overflow-hidden bg-[#eef2fb] dark:bg-claude-darkBg">
      {/* 点击空白处关闭下拉菜单 */}
      {anyMenuOpen && <div className="fixed inset-0 z-10" onClick={closeMenus} />}

      {/* ── Code 专属侧边栏 ── */}
      <aside
        className={`${isMobile || collapsed ? 'hidden' : 'flex'} w-[248px] shrink-0 flex-col overflow-hidden rounded-[22px] bg-white dark:bg-claude-darkSurface`}
        style={{ boxShadow: cardShadow }}
      >
        {/* 品牌 + 返回 */}
        <div className="flex items-center gap-2 px-3.5 pt-3.5 pb-2.5">
          <button type="button" onClick={onBack}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover"
            title={i18nService.t('back')}>
            {Ic.back}
          </button>
          <BoclawLogo size={24} showText={false} />
          <span className="text-[15px] font-medium text-claude-text dark:text-claude-darkText">
            Bo<b className="font-bold" style={{ color: BLUE }}>Coder</b>
          </span>
          <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ color: BLUE, background: BLUE_TINT }}>
            {i18nService.t('codeBetaBadge')}
          </span>
          <button type="button" onClick={() => setCollapsed(true)} title={i18nService.t('codeCollapseSidebar')}
            className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover">
            {Ic.sidebar}
          </button>
        </div>

        {/* 主导航 */}
        <div className="space-y-0.5 px-2.5 pb-1">
          <CodeNavItem icon={Ic.term} label={i18nService.t('codeNavNewSession')} active={nav === 'sessions'} onClick={() => { setNav('sessions'); newSession(); }} />
          <CodeNavItem icon={<SkillCenterIcon className="h-[18px] w-[18px]" />} label={i18nService.t('codeNavSkills')} active={nav === 'skills'} onClick={() => setNav('skills')} />
          <CodeNavItem icon={Ic.clock} label={i18nService.t('codeNavTasks')} active={nav === 'tasks'} onClick={() => setNav('tasks')} />
        </div>
        <div className="mx-3.5 my-1.5 border-t border-black/[0.06] dark:border-white/10" />

        {/* 设备选择 + 会话列表常驻：切到 Skills / 定时任务时仍保留，方便随时切回会话 */}
        {/* 设备选择 */}
        <div className="relative px-2.5 pb-1.5">
          <button type="button" onClick={() => setDeviceMenu((v) => !v)}
            className="flex w-full items-center gap-2 rounded-lg border border-black/[0.06] dark:border-white/10 px-2.5 py-2 text-sm text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
            <span style={{ color: BLUE }}>{isCloud ? Ic.cloud : Ic.laptop}</span>
            <span className="flex-1 truncate text-left">
              {selectedClient
                ? (isCloud ? i18nService.t('codeCloudEnv') : (selectedClient.deviceName || platformLabel(selectedClient.platform)))
                : i18nService.t('codeDeviceUnconnected')}
            </span>
            {!isCloud && (
              <span className={`h-1.5 w-1.5 rounded-full ${desktops.length ? '' : 'opacity-40'}`} style={{ background: desktops.length ? '#22c55e' : '#c2c7cf' }} />
            )}
            {Ic.caretd}
          </button>
          {deviceMenu && (
            <div className="absolute left-2.5 right-2.5 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-black/[0.08] dark:border-white/10 bg-white dark:bg-claude-darkSurface py-1 shadow-lg">
              {/* 云端环境置顶项 */}
              {cloudClient && (
                <button type="button"
                  onClick={() => { dispatch(setSelectedClient(cloudClient.clientId)); setDeviceMenu(false); }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover ${cloudClient.clientId === selectedClientId ? 'font-medium' : ''}`}
                  style={cloudClient.clientId === selectedClientId ? { color: BLUE } : undefined}>
                  <span style={{ color: BLUE }}>{Ic.cloud}</span>
                  <span className="flex-1 truncate text-left text-claude-text dark:text-claude-darkText">{i18nService.t('codeCloudEnv')}</span>
                  <span className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('codeCloudEnvTag')}</span>
                </button>
              )}
              {cloudClient && desktops.length > 0 && (
                <div className="mx-3 my-1 border-t border-black/[0.06] dark:border-white/10" />
              )}
              {desktops.length === 0 && !cloudClient && (
                <div className="px-3 py-2 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                  {i18nService.t('codeNoOnlineDesktop')}
                </div>
              )}
              {desktops.map((c) => (
                <button key={c.clientId} type="button"
                  onClick={() => { dispatch(setSelectedClient(c.clientId)); setDeviceMenu(false); }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover ${c.clientId === selectedClientId ? 'font-medium' : ''}`}
                  style={c.clientId === selectedClientId ? { color: BLUE } : undefined}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#22c55e' }} />
                  <span className="flex-1 truncate text-left text-claude-text dark:text-claude-darkText">{c.deviceName || platformLabel(c.platform)}</span>
                  <span className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{platformLabel(c.platform)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 会话列表头 */}
        <div className="flex items-center justify-between px-3.5 pt-1 pb-1">
          <span className="text-sm font-semibold text-claude-text dark:text-claude-darkText">{i18nService.t('codeNavSessions')}</span>
          <div className="flex items-center gap-0.5">
            <button type="button" onClick={() => { setManageMode((v) => !v); setSelectedIds(new Set()); }}
              className="rounded-md px-1.5 py-0.5 text-xs text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover">
              {manageMode ? i18nService.t('codeManageDone') : i18nService.t('codeManage')}
            </button>
          </div>
        </div>

        {/* 搜索框 */}
        {sessions.length > 0 && (
          <div className="px-2.5 pb-1.5">
            <div className="flex items-center gap-1.5 rounded-lg border border-black/[0.06] dark:border-white/10 px-2.5 py-1.5">
              <span className="text-claude-textSecondary dark:text-claude-darkTextSecondary">{Ic.search}</span>
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={i18nService.t('codeSearchPlaceholder')}
                className="w-full bg-transparent text-xs text-claude-text dark:text-claude-darkText placeholder:text-claude-textSecondary dark:placeholder:text-claude-darkTextSecondary outline-none" />
            </div>
          </div>
        )}

        {/* 会话列表 */}
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {sessions.length === 0 && (
            <div className="px-2.5 py-6 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {selectedClientId ? i18nService.t('codeNoSessions') : i18nService.t('codeSelectDeviceFirst')}
            </div>
          )}
          {sessions.length > 0 && filteredSessions.length === 0 && (
            <div className="px-2.5 py-6 text-center text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {i18nService.t('codeNoSessionMatch')}
            </div>
          )}
          {sortedSessions.map((s) => {
            const active = s.id === currentSessionId;
            const st = statusBySession[s.id] ?? s.status;
            const dot = st === 'error' ? '#ef4444' : st === 'stopped' ? '#c2c7cf' : BLUE;
            const isPinned = pinned.has(s.id);
            const checked = selectedIds.has(s.id);
            return (
              <div key={s.id}
                onClick={() => (manageMode ? toggleSelect(s.id) : openSession(s.id))}
                className={`group relative flex min-h-[40px] items-center gap-2 rounded-lg px-2.5 py-2 text-sm cursor-pointer ${active && !manageMode ? 'bg-claude-accent/10' : 'hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover'}`}>
                {manageMode ? (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border"
                    style={checked ? { background: BLUE, borderColor: BLUE, color: '#fff' } : { borderColor: 'rgba(0,0,0,0.2)' }}>
                    {checked && Ic.check}
                  </span>
                ) : (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dot }} />
                )}
                {isPinned && !manageMode && <span className="shrink-0" style={{ color: BLUE }}>{Ic.pin}</span>}
                <span title={s.title || i18nService.t('codeUntitledSession')} className={`flex-1 truncate text-claude-text dark:text-claude-darkText ${active ? 'font-medium' : ''}`}>{s.title || i18nService.t('codeUntitledSession')}</span>
                {!manageMode && (
                  <>
                    <span className="shrink-0 text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary group-hover:hidden">{relTime(s.updatedAt)} · {statusText(st)}</span>
                    <button type="button" title={i18nService.t('codeMore')}
                      onClick={(e) => { e.stopPropagation(); setMenuOpenId((cur) => (cur === s.id ? null : s.id)); }}
                      className="hidden h-6 w-6 shrink-0 items-center justify-center rounded text-claude-textSecondary hover:bg-black/5 dark:hover:bg-white/10 group-hover:flex">
                      {Ic.dots}
                    </button>
                    {menuOpenId === s.id && (
                      <div className="absolute right-2 top-9 z-30 min-w-28 rounded-xl border border-black/[0.08] dark:border-white/10 bg-white dark:bg-claude-darkSurface py-1 shadow-lg"
                        onClick={(e) => e.stopPropagation()}>
                        <MenuRow icon={Ic.pin} label={isPinned ? i18nService.t('codeUnpin') : i18nService.t('codePin')} onClick={() => { togglePin(s.id); setMenuOpenId(null); }} />
                        <MenuRow icon={Ic.edit} label={i18nService.t('codeRename')} onClick={() => { setMenuOpenId(null); renameSession(s); }} />
                        <MenuRow icon={Ic.trash} label={i18nService.t('codeDeleteSession')} danger onClick={(e) => { setMenuOpenId(null); deleteSession(s.id, e); }} />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* 管理模式底部栏 */}
        {manageMode && (
          <div className="flex items-center gap-2 border-t border-black/[0.06] dark:border-white/10 px-3 py-2">
            <button type="button" onClick={selectAllToggle}
              className="rounded-md px-2 py-1 text-xs text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover">
              {i18nService.t('codeSelectAll')}
            </button>
            <button type="button" onClick={batchDelete} disabled={!selectedIds.size}
              className="ml-auto rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
              style={{ background: '#ef4444' }}>
              {i18nService.t('codeBatchDelete')}{selectedIds.size ? ` (${selectedIds.size})` : ''}
            </button>
          </div>
        )}

        {/* 用户卡（点击进设置）*/}
        <button type="button" onClick={() => onShowSettings?.()}
          className="group mx-2 mb-2 mt-auto flex items-center gap-2.5 rounded-xl border border-black/[0.06] dark:border-white/10 px-2.5 py-2 text-left hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg"><BoclawLogo size={28} showText={false} /></div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-claude-text dark:text-claude-darkText">{phone || accountLabel}</div>
            <div className="truncate text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{authState?.user.username}</div>
          </div>
          <span className="shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary opacity-0 transition-opacity group-hover:opacity-100">{Ic.gear}</span>
        </button>
      </aside>

      {/* ── 主区 ── */}
      {nav === 'skills' ? (
        <main className="flex flex-1 min-w-0 flex-col overflow-hidden rounded-[22px] bg-white dark:bg-claude-darkSurface" style={{ boxShadow: cardShadow }}>
          <SkillsView isSidebarCollapsed onToggleSidebar={() => {}} onNewChat={() => setNav('sessions')} />
        </main>
      ) : nav === 'tasks' ? (
        <main className="flex flex-1 min-w-0 flex-col overflow-hidden rounded-[22px] bg-white dark:bg-claude-darkSurface" style={{ boxShadow: cardShadow }}>
          <ScheduledTasksView isSidebarCollapsed onToggleSidebar={() => {}} onNewChat={() => setNav('sessions')} />
        </main>
      ) : (
      <main className="flex flex-1 min-w-0 flex-col overflow-hidden rounded-[22px] bg-white dark:bg-claude-darkSurface" style={{ boxShadow: cardShadow }}>
        {/* 顶栏 */}
        <div className="flex items-center gap-3 border-b border-black/[0.05] dark:border-white/10 px-4 py-2.5">
          {isMobile && (
            <button type="button" onClick={onBack} className="flex h-7 w-7 items-center justify-center rounded-lg text-claude-textSecondary dark:text-claude-darkTextSecondary">{Ic.back}</button>
          )}
          {collapsed && !isMobile && (
            <button type="button" onClick={() => setCollapsed(false)} title={i18nService.t('codeExpandSidebar')}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover">{Ic.sidebar}</button>
          )}
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {currentSession && (
              <span className="max-w-[45%] shrink-0 truncate text-sm font-medium text-claude-text dark:text-claude-darkText">
                {currentSession.title || i18nService.t('codeUntitledSession')}
              </span>
            )}
            <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {Ic.folder}
              <span className="truncate font-mono">{currentSession?.repoUrl || (currentSession as any)?.gitRepoUrl || currentSession?.cwd || i18nService.t('codeNewSessionCwd')}</span>
            </div>
          </div>
          {/* 终端开关:仅云端、且沙箱正在运行时展示 */}
          {isCloud && currentSessionId && sandboxLive && (
            <button
              type="button"
              onClick={() => setShowTerminal((v) => { if (!v) setTerminalOpenedOnce(true); return !v; })}
              title={i18nService.t('coworkTerminalTitle')}
              aria-label={i18nService.t('coworkTerminalTitle')}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                showTerminal
                  ? 'bg-claude-surfaceHover text-claude-text dark:bg-claude-darkSurfaceHover dark:text-claude-darkText'
                  : 'text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover'
              }`}
            >
              {Ic.term}
            </button>
          )}
        </div>

        {/* 主体:左侧聊天 + 右侧终端分栏 */}
        <div ref={splitRowRef} className="relative flex flex-1 min-h-0 flex-row">
        <div className="flex flex-1 min-w-0 min-h-0 flex-col">
        {/* 聊天区 */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5">
          <div className="mx-auto flex max-w-3xl flex-col gap-3">
            {messages.length === 0 && (
              <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-claude-surfaceMuted/70 dark:bg-claude-darkSurfaceMuted">
                  <BoclawLogo size={44} showText={false} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="text-[15px] font-semibold text-claude-text dark:text-claude-darkText">
                    {selectedClient ? i18nService.t('codeEmptyTitle') : i18nService.t('codeSelectDesktopToStart')}
                  </div>
                  <div className="max-w-[320px] text-[13px] leading-relaxed text-claude-textSecondary dark:text-claude-darkTextSecondary">
                    {i18nService.t(isCloud ? 'codeCloudEmptyHint' : 'codeEmptyHint')}
                  </div>
                </div>
              </div>
            )}
            {renderMessages(messages, running)}
          </div>
        </div>

        {/* 抽屉锚点放在输入卡上沿，避免外层 padding 造成缝隙。 */}
        <div className="border-t border-black/[0.05] dark:border-white/10 px-4 py-3">
          {pendingPermission && (
            <FollowupDrawer key={currentSessionId + ':' + pendingPermission.requestId} language={i18nService.getLanguage()}>
              {pendingPermission.toolName === 'AskUserQuestion'
                && parseAskUserQuestions(pendingPermission.toolInput).length > 0 ? (
                // AskUserQuestion 不是「允许/拒绝」式权限,而是 agent 在提问:渲染成问题选择器,
                // 选完答案通过 updatedInput.answers 回传(其余工具在沙箱侧已自动放行)。
                <CodingQuestionPrompt
                  permission={pendingPermission}
                  onSubmit={(updatedInput) => respondPermission('allow', { updatedInput })}
                  onDeny={() => respondPermission('deny')}
                />
              ) : (
                <div className="mx-auto max-w-3xl rounded-xl border border-black/[0.08] dark:border-white/10 bg-claude-surfaceMuted/60 dark:bg-claude-darkSurfaceMuted p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-claude-text dark:text-claude-darkText">
                    <span style={{ color: BLUE }}>{Ic.shield}</span>
                    {i18nService.t('codePermissionTool')}：<span className="font-mono">{pendingPermission.toolName}</span>
                  </div>
                  <pre className="mt-2 max-h-28 overflow-auto rounded-lg bg-black/[0.04] dark:bg-white/5 p-2 text-[12px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                    {(() => { try { return JSON.stringify(pendingPermission.toolInput, null, 2); } catch { return String(pendingPermission.toolInput); } })()}
                  </pre>
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" onClick={() => respondPermission('allow')} className="rounded-lg px-3 py-1.5 text-sm font-medium text-white" style={{ background: BLUE }}>{i18nService.t('codeAllow')}</button>
                    {pendingPermission.supportsAllowAlways && (
                      <button type="button" onClick={() => respondPermission('allow_always')} className="rounded-lg border border-black/[0.1] dark:border-white/15 px-3 py-1.5 text-sm text-claude-text dark:text-claude-darkText">{i18nService.t('codeAllowAlways')}</button>
                    )}
                    <button type="button" onClick={() => respondPermission('deny')} className="rounded-lg border border-black/[0.1] dark:border-white/15 px-3 py-1.5 text-sm text-claude-text dark:text-claude-darkText">{i18nService.t('codeDeny')}</button>
                  </div>
                </div>
              )}
            </FollowupDrawer>
          )}

          <div className="rounded-2xl border border-black/[0.08] dark:border-white/10 bg-white dark:bg-claude-darkSurfaceMuted px-3.5 py-3">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={selectedClientId ? i18nService.t('codeComposerPlaceholder') : i18nService.t('codeComposerDisabled')}
              disabled={!selectedClientId}
              className="max-h-40 w-full resize-none bg-transparent text-sm text-claude-text dark:text-claude-darkText placeholder:text-claude-textSecondary dark:placeholder:text-claude-darkTextSecondary outline-none"
            />
            <div className="mt-3 flex items-center gap-2">
              {/* 模型选择器 */}
              {models.length > 0 && (
                <div className="relative shrink-0">
                  <button type="button" onClick={() => setModelMenu((v) => !v)}
                    className="flex items-center gap-1.5 rounded-lg border border-black/[0.08] dark:border-white/10 px-2.5 py-1.5 text-xs text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
                    {Ic.model}{curModelName} {Ic.caretd}
                  </button>
                  {modelMenu && (
                    <div className="absolute bottom-full left-0 z-20 mb-1 max-h-72 min-w-44 overflow-y-auto rounded-xl border border-black/[0.08] dark:border-white/10 bg-white dark:bg-claude-darkSurface py-1 shadow-lg">
                      {models.map((m) => (
                        <button key={m.id} type="button" onClick={() => changeModel(m.id)}
                          className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover ${m.id === curModelId ? 'font-medium' : 'text-claude-text dark:text-claude-darkText'}`}
                          style={m.id === curModelId ? { color: BLUE } : undefined}>{m.name}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* 执行模式选择器（仅 desktop/relay 会话；云端沙箱自动放行权限，不显示）*/}
              {!isCloud && (
                <div className="relative shrink-0">
                  <button type="button" onClick={() => setModeMenu((v) => !v)}
                    title={i18nService.t('codeExecutionMode')}
                    className="flex items-center gap-1.5 rounded-lg border border-black/[0.08] dark:border-white/10 px-2.5 py-1.5 text-xs text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
                    {Ic.shield}{permModeLabel(curMode)} {Ic.caretd}
                  </button>
                  {modeMenu && (
                    <div className="absolute bottom-full left-0 z-20 mb-1 max-h-72 min-w-44 overflow-y-auto rounded-xl border border-black/[0.08] dark:border-white/10 bg-white dark:bg-claude-darkSurface py-1 shadow-lg">
                      {permissionModes.map((m) => (
                        <button key={m} type="button" onClick={() => changeMode(m)}
                          className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover ${m === curMode ? 'font-medium' : 'text-claude-text dark:text-claude-darkText'}`}
                          style={m === curMode ? { color: BLUE } : undefined}>{permModeLabel(m)}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* 工作目录选择（仅桌面新建会话；浏览 desktop 目录树后塞进 start 的 cwd）*/}
              {!isCloud && !currentSessionId && (() => {
                // 未手选时展示 desktop 默认目录（config.defaultCwd）；都没有再退回「默认目录」文案
                const effectiveCwd = pickedCwd || config?.defaultCwd || '';
                const baseName = effectiveCwd.replace(/[\\/]+$/, '').split(/[\\/]/).pop() || '';
                return (
                  <button type="button" onClick={() => setCwdPickerOpen(true)}
                    title={effectiveCwd || i18nService.t('codeCwdTitle')}
                    className="flex min-w-0 shrink items-center gap-1.5 rounded-lg border border-black/[0.08] dark:border-white/10 px-2.5 py-1.5 text-xs text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
                    {Ic.folder}
                    <span className="max-w-[160px] truncate font-mono">
                      {baseName || i18nService.t('codeCwdDefaultLabel')}
                    </span>
                    {Ic.caretd}
                  </button>
                );
              })()}
              {/* 新建云端会话：从 Git 仓库开始，可填写 HTTPS 认证凭证 */}
              {isCloud && !currentSessionId && (
                <>
                  <div className="flex min-w-0 flex-1 max-w-[360px] items-center gap-2 rounded-lg border border-black/[0.08] dark:border-white/10 bg-claude-surfaceMuted/40 dark:bg-claude-darkSurfaceMuted px-2.5 py-1.5">
                    <span className="shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary" title={i18nService.t('codeGitRepoLabel')}>{Ic.term}</span>
                    <input
                      value={gitRepoUrl}
                      onChange={(e) => setGitRepoUrl(e.target.value)}
                      placeholder={i18nService.t('codeGitRepoPlaceholder')}
                      className="min-w-0 flex-1 bg-transparent text-xs text-claude-text dark:text-claude-darkText placeholder:text-claude-textSecondary dark:placeholder:text-claude-darkTextSecondary outline-none"
                    />
                  </div>
                  <input
                    value={gitBranch}
                    onChange={(e) => setGitBranch(e.target.value)}
                    placeholder={i18nService.t('codeGitBranchPlaceholder')}
                    className="h-[34px] w-20 shrink-0 rounded-lg border border-black/[0.08] dark:border-white/10 bg-claude-surfaceMuted/40 dark:bg-claude-darkSurfaceMuted px-2.5 text-xs text-claude-text dark:text-claude-darkText placeholder:text-claude-textSecondary dark:placeholder:text-claude-darkTextSecondary outline-none"
                  />
                  <input
                    value={gitUsername}
                    onChange={(e) => setGitUsername(e.target.value)}
                    placeholder={i18nService.t('codeGitUsernamePlaceholder')}
                    autoComplete="off"
                    className="h-[34px] w-24 shrink-0 rounded-lg border border-black/[0.08] dark:border-white/10 bg-claude-surfaceMuted/40 dark:bg-claude-darkSurfaceMuted px-2.5 text-xs text-claude-text dark:text-claude-darkText placeholder:text-claude-textSecondary dark:placeholder:text-claude-darkTextSecondary outline-none"
                  />
                  <input
                    type="password"
                    value={gitPassword}
                    onChange={(e) => setGitPassword(e.target.value)}
                    placeholder={i18nService.t('codeGitPasswordPlaceholder')}
                    autoComplete="off"
                    className="h-[34px] w-28 shrink-0 rounded-lg border border-black/[0.08] dark:border-white/10 bg-claude-surfaceMuted/40 dark:bg-claude-darkSurfaceMuted px-2.5 text-xs text-claude-text dark:text-claude-darkText placeholder:text-claude-textSecondary dark:placeholder:text-claude-darkTextSecondary outline-none"
                  />
                </>
              )}
              <div className="ml-auto flex shrink-0 items-center gap-2">
                {running ? (
                  <button type="button" onClick={stop} title={i18nService.t('codeStop')}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white transition-colors hover:bg-red-600">
                    {Ic.stop}
                  </button>
                ) : (
                  <button type="button" onClick={send} disabled={!input.trim() || !selectedClientId || (isCloud && !currentSessionId && !gitRepoUrl.trim())}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-40"
                    style={{ background: BLUE }}>
                    {Ic.send}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* 拖拽分隔条:调整终端宽度(仅桌面、终端展开时)*/}
        {showTerminal && terminalOpenedOnce && currentSessionId && isCloud && sandboxLive && !isMobile && (
          <div
            onMouseDown={onTerminalDragStart}
            className="group relative w-1 shrink-0 cursor-col-resize select-none"
            title={i18nService.t('coworkTerminalResizeHint')}
          >
            <div className="absolute inset-y-0 -left-1 -right-1" />
            <div className="h-full w-full bg-black/[0.05] transition-colors group-hover:bg-emerald-500 group-active:bg-emerald-500 dark:bg-white/10" />
          </div>
        )}

        {/* 终端面板:打开过就保持挂载(收起=hidden),重开保留同一 shell 与历史 */}
        {terminalOpenedOnce && currentSessionId && isCloud && sandboxLive && (
          <div
            className={`flex flex-col min-h-0 min-w-0 border-l border-black/[0.05] dark:border-white/10 ${
              !showTerminal ? 'hidden' : isMobile ? 'absolute inset-0 z-30 bg-white dark:bg-claude-darkSurface' : 'shrink-0'
            }`}
            style={!showTerminal || isMobile ? undefined : { width: terminalWidth }}
          >
            <TerminalPanel sessionId={currentSessionId} hideHeader onClose={() => setShowTerminal(false)} />
          </div>
        )}
        </div>

        {/* 工作目录选择弹窗（仅桌面会话）*/}
        {cwdPickerOpen && selectedClientId && !isCloud && (
          <CwdPickerModal
            clientId={selectedClientId}
            initialPath={pickedCwd || config?.defaultCwd}
            onSelect={(p) => { setPickedCwd(p || null); setCwdPickerOpen(false); }}
            onClose={() => setCwdPickerOpen(false)}
          />
        )}
      </main>
      )}
    </div>
  );
};

// ── 单条消息渲染 ──
function MessageItem({ m, active }: { m: CodingMessage; active?: boolean }) {
  if (m.type === 'user') {
    return <div className="self-end max-w-[80%] rounded-2xl px-4 py-2.5 text-sm text-white whitespace-pre-wrap" style={{ background: BLUE }}>{m.content}</div>;
  }
  if (m.type === 'tool_use') {
    const name = m.metadata?.toolName || 'tool';
    let inputStr = '';
    try { inputStr = JSON.stringify(m.metadata?.toolInput ?? {}, null, 2); } catch { inputStr = ''; }
    return (
      <div className="rounded-xl border border-black/[0.06] dark:border-white/10 bg-claude-surfaceMuted/60 dark:bg-claude-darkSurfaceMuted px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-claude-text dark:text-claude-darkText">
          <span style={{ color: BLUE }}>{Ic.term}</span>{name}
        </div>
        {inputStr && inputStr !== '{}' && (
          <pre className="mt-1.5 max-h-40 overflow-auto rounded-lg bg-black/[0.04] dark:bg-white/5 p-2 text-[12px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{inputStr}</pre>
        )}
      </div>
    );
  }
  if (m.type === 'tool_result') {
    const err = m.metadata?.isError;
    if (!m.content) return null;
    return (
      <pre className={`max-h-48 overflow-auto rounded-xl px-3 py-2 text-[12px] ${err ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-black/[0.04] dark:bg-white/5 text-claude-textSecondary dark:text-claude-darkTextSecondary'}`}>{m.content}</pre>
    );
  }
  // assistant
  const thinking = m.metadata?.isThinking;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
        {active
          ? <Spinner size={13} />
          : <span style={{ color: '#22c55e' }}>{Ic.checkCircle}</span>}
        <span className="font-medium text-claude-text dark:text-claude-darkText">Code</span>
        {active && <span className="inline-flex gap-0.5 [&>span]:animate-bounce"><span>·</span><span style={{ animationDelay: '0.15s' }}>·</span><span style={{ animationDelay: '0.3s' }}>·</span></span>}
        {thinking && <span>· {i18nService.t('codeThinking')}</span>}
      </div>
      {thinking ? (
        <div className="text-sm italic text-claude-textSecondary dark:text-claude-darkTextSecondary whitespace-pre-wrap">{m.content}</div>
      ) : (
        <MarkdownContent content={m.content} className="text-claude-text dark:text-claude-darkText" />
      )}
    </div>
  );
}

// 连续的工具消息聚成一个默认折叠的「工具调用」卡；思考消息各自渲染成可折叠组件
// （无外层包裹卡）；真正的回答正文(非 thinking 的 assistant)和 user/system 单独展示。
function renderMessages(messages: CodingMessage[], running = false): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let buf: CodingMessage[] = [];
  let bufKey = 0;
  const flush = () => {
    if (buf.length) { out.push(<StepGroup key={`sg-${bufKey}`} items={buf} />); buf = []; }
  };
  const lastIdx = messages.length - 1;
  messages.forEach((m, i) => {
    if (m.type === 'tool_use' || m.type === 'tool_result') {
      if (buf.length === 0) bufKey = i;
      buf.push(m);
    } else {
      flush();
      if (m.type === 'assistant' && m.metadata?.isThinking) {
        out.push(<ThinkingItem key={m.id} m={m} active={running && i === lastIdx} />);
      } else {
        // 正在生成的那条回答正文（最后一条 assistant）显示动效
        const active = running && i === lastIdx && m.type === 'assistant';
        out.push(<MessageItem key={m.id} m={m} active={active} />);
      }
    }
  });
  flush();
  // 运行中但最后一条是内部过程 / 非回答正文 → 末尾追加思考指示
  if (running) {
    const last = messages[lastIdx];
    if (!last || last.type !== 'assistant' || last.metadata?.isThinking) out.push(<TypingIndicator key="__typing" />);
  }
  return out;
}

// 思考过程：自身可折叠（无外层卡），点标题展开/收起思考文本。默认折叠。
// 思考中显示旋转 Spinner，完成后换成静态灯泡图标；展开/收起箭头放在标题右侧。
function ThinkingItem({ m, active = false }: { m: CodingMessage; active?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 -mx-1.5 rounded-md px-1.5 py-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]">
        <span style={{ color: BLUE }}>{active ? <Spinner size={15} /> : Ic.bulb}</span>
        <span className="font-medium text-claude-text dark:text-claude-darkText">{i18nService.t('codeThinkingProcess')}</span>
        <span className="ml-auto flex items-center gap-1">
          {!open && <span className="text-[11px]">{i18nService.t('codeClickExpand')}</span>}
          <span className={`transition-transform ${open ? 'rotate-90' : ''}`} style={{ color: BLUE }}>{Ic.chevron}</span>
        </span>
      </button>
      {open && (
        <div className="pl-5 text-sm italic text-claude-textSecondary dark:text-claude-darkTextSecondary whitespace-pre-wrap">{m.content}</div>
      )}
    </div>
  );
}

// 等待输出时的思考指示（旋转 + 跳动小点）
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
      <Spinner size={13} />
      <span className="font-medium text-claude-text dark:text-claude-darkText">Code</span>
      <span className="inline-flex gap-0.5 [&>span]:animate-bounce">
        <span>·</span><span style={{ animationDelay: '0.15s' }}>·</span><span style={{ animationDelay: '0.3s' }}>·</span>
      </span>
    </div>
  );
}

// 工具调用折叠卡（默认折叠）
function StepGroup({ items }: { items: CodingMessage[] }) {
  const [open, setOpen] = useState(false);
  const count = items.filter((x) => x.type === 'tool_use').length || items.length;
  return (
    <div className="flex flex-col gap-1">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 -mx-1.5 rounded-md px-1.5 py-1 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]">
        <span style={{ color: BLUE }}>{Ic.wrench}</span>
        <span className="font-medium text-claude-text dark:text-claude-darkText">{i18nService.t('codeAgentWorking')}</span>
        <span className="flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-medium" style={{ color: BLUE, background: BLUE_TINT }}>{count}</span>
        <span className="ml-auto flex items-center gap-1">
          {!open && <span className="text-[11px]">{i18nService.t('codeClickExpand')}</span>}
          <span className={`transition-transform ${open ? 'rotate-90' : ''}`} style={{ color: BLUE }}>{Ic.chevron}</span>
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-1.5 pl-5">
          {pairToolItems(items).map((p, i) => <ToolPair key={p.use?.id || p.result?.id || i} use={p.use} result={p.result} />)}
        </div>
      )}
    </div>
  );
}

// 把一段工具消息按 toolUseId 配对成 输入(tool_use)→输出(tool_result)；
// 无法配对的输出单独成对（use 为空）。
function pairToolItems(items: CodingMessage[]): { use: CodingMessage | null; result: CodingMessage | null }[] {
  const results = new Map<string, CodingMessage>();
  for (const x of items) {
    if (x.type === 'tool_result' && x.metadata?.toolUseId) results.set(x.metadata.toolUseId, x);
  }
  const consumed = new Set<string>();
  const pairs: { use: CodingMessage | null; result: CodingMessage | null }[] = [];
  for (const m of items) {
    if (m.type === 'tool_use') {
      const res = m.metadata?.toolUseId ? results.get(m.metadata.toolUseId) : undefined;
      if (res) consumed.add(res.id);
      pairs.push({ use: m, result: res || null });
    }
  }
  for (const m of items) {
    if (m.type === 'tool_result' && !consumed.has(m.id)) pairs.push({ use: null, result: m });
  }
  return pairs;
}

// 单个工具调用：输入与输出一一对应，默认折叠，点标题展开。
function ToolPair({ use, result }: { use: CodingMessage | null; result: CodingMessage | null }) {
  const [open, setOpen] = useState(false);
  const name = use?.metadata?.toolName || result?.metadata?.toolName || 'tool';
  let inputStr = '';
  try { inputStr = JSON.stringify(use?.metadata?.toolInput ?? {}, null, 2); } catch { inputStr = ''; }
  const hasInput = !!inputStr && inputStr !== '{}';
  const err = result?.metadata?.isError;
  return (
    <div className="rounded-lg border border-black/[0.06] dark:border-white/10 bg-claude-surfaceMuted/50 dark:bg-claude-darkSurfaceMuted/60">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-claude-text dark:text-claude-darkText cursor-pointer transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
        <span style={{ color: BLUE }}>{Ic.term}</span>
        <span className="font-medium">{name}</span>
        {err && <span className="text-[10px] text-red-500">●</span>}
        <span className={`ml-auto transition-transform ${open ? 'rotate-90' : ''}`} style={{ color: BLUE }}>{Ic.chevron}</span>
      </button>
      {open && (
        <div className="flex flex-col gap-1.5 border-t border-black/[0.06] dark:border-white/10 px-2.5 py-2">
          {hasInput && (
            <pre className="max-h-40 overflow-auto rounded-md bg-black/[0.04] dark:bg-white/5 p-2 text-[12px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{inputStr}</pre>
          )}
          {result?.content && (
            <pre className={`max-h-48 overflow-auto rounded-md px-2 py-1.5 text-[12px] ${err ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-black/[0.04] dark:bg-white/5 text-claude-textSecondary dark:text-claude-darkTextSecondary'}`}>{result.content}</pre>
          )}
          {!hasInput && !result?.content && (
            <div className="text-[12px] italic text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('codeNoOutput')}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default CodingView;
